import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  SelectMenu,
  BodyText,
  Checkbox,
  showToast,
} from '@spotoninc/nexus-react';
import { useNexus } from '@spotoninc/nexus-react';
import { useSearchParams } from 'react-router-dom';
import { useHotglue } from '@hotglue/widget';
import '../../../styles/reskinned-integration.css';
import { trackIntegrationAdded } from '../../../utils/analytics/integrationTracking';
import { fetchWithAuth } from '../../../utils/apiHelpers';
import { redirectToIntuitOAuth } from '../../../utils/intuitSessionUtils';

// Integration types that use simplified location setup (checkbox only, no dropdown)
const SIMPLIFIED_LOCATION_TYPES = ['rest365', 'xero'];

// Map query param values to integration type values - defined outside component to prevent re-renders
const INTEGRATION_TYPE_MAP = {
  QBOnline: 'online',
  xero: 'xero',
  R365: 'rest365',
};

// Hardcoded integration types for this modal
const INTEGRATION_TYPE_OPTIONS = [
  { value: 'online', label: 'QuickBooks Online' },
  { value: 'xero', label: 'Xero' },
  { value: 'rest365', label: 'Restaurant365' },
];

/**
 * Reskinned Add Integration Flow Modal Component
 * New integration flow with checkboxes for Period Types and Location Setup
 * Based on Figma designs with Aero Design components
 */
// Hardcoded period types for this modal
const PERIOD_TYPE_OPTIONS = [
  { value: '13 periods', label: '13 periods' },
  { value: '4-4-5', label: '4-4-5' },
  { value: '4-5-4', label: '4-5-4' },
  { value: '5-4-4', label: '5-4-4' },
];

const ReskinnedAddIntegrationFlowModal = ({
  isOpen,
  onClose,
  onSave,
  defaultPeriod,
  allIntegrationTypes,
  isFirstIntegration,
  apps, // Array of existing integrations - used for naming
  companyName, // Company name from metadata - used for first integration naming
}) => {
  const { theme } = useNexus();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // HotGlue hook for non-QuickBooks integrations
  const { link } = useHotglue();

  // Form state
  const [integrationType, setIntegrationType] = useState('');
  const [integrationTypeName, setIntegrationTypeName] = useState('');
  const [showPeriodTypes, setShowPeriodTypes] = useState(false);
  const [periodCalType, setPeriodCalType] = useState(defaultPeriod);
  const [showLocationSetup, setShowLocationSetup] = useState(false);
  const [locationSetup, setLocationSetup] = useState('Neither');

  // HotGlue connection details from allIntegrationTypes
  const [hotglueFlowId, setHotglueFlowId] = useState('');
  const [sourceId, setSourceId] = useState('');

  // Check if current integration type uses simplified location setup
  const useSimplifiedLocationSetup =
    SIMPLIFIED_LOCATION_TYPES.includes(integrationType);

  // Reset form state helper function - uses useCallback with defaultPeriod dependency
  // to ensure the current defaultPeriod value is used even if it changes after component mounts
  const resetFormState = useCallback(() => {
    setIntegrationType('');
    setIntegrationTypeName('');
    setShowPeriodTypes(false);
    setPeriodCalType(defaultPeriod);
    setShowLocationSetup(false);
    setLocationSetup('Neither');
    setHotglueFlowId('');
    setSourceId('');
    setIsLoading(false);
    setIsFormValid(false);
  }, [defaultPeriod]);

  // Handle modal close with form reset
  const handleClose = () => {
    resetFormState();
    onClose();
  };

  // Format data for Nexus SelectMenu
  const formatSelectData = data => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data.map(item => ({
      value: item.value,
      label: item.label,
    }));
  };

  // Define handleIntegrationTypeChange before the useEffect that uses it
  const handleIntegrationTypeChange = useCallback(
    value => {
      setIntegrationType(value);

      // Find the display name from hardcoded INTEGRATION_TYPE_OPTIONS
      const selectedType = INTEGRATION_TYPE_OPTIONS.find(
        item => item.value === value
      );
      if (selectedType) {
        setIntegrationTypeName(selectedType.label);
      }

      // Get HotGlue flow_id and source_id from allIntegrationTypes
      if (allIntegrationTypes) {
        const fullTypeInfo = allIntegrationTypes.find(
          item => item.integration_type === value
        );
        if (fullTypeInfo) {
          setHotglueFlowId(fullTypeInfo.flow_id || '');
          setSourceId(fullTypeInfo.source_id || '');
        }
      }
    },
    [allIntegrationTypes]
  );

  // Reset form and auto-select integration type from URL query parameter when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal opens to ensure clean slate
      resetFormState();

      const integrationTypeParam = searchParams.get('integrationType');
      if (integrationTypeParam && INTEGRATION_TYPE_MAP[integrationTypeParam]) {
        const mappedType = INTEGRATION_TYPE_MAP[integrationTypeParam];
        handleIntegrationTypeChange(mappedType);
      }
    }
  }, [isOpen, searchParams, resetFormState, handleIntegrationTypeChange]);

  // Check form validity whenever required fields change
  useEffect(() => {
    const isValid = integrationType !== '';
    setIsFormValid(isValid);
  }, [integrationType]);

  // Initialize periodCalType with defaultPeriod on mount or when defaultPeriod first becomes available
  // Only run when defaultPeriod changes to avoid resetting user selections
  useEffect(() => {
    if (defaultPeriod) {
      setPeriodCalType(prevValue => prevValue || defaultPeriod);
    }
  }, [defaultPeriod]);

  const handleSave = async () => {
    setIsLoading(true);

    if (!isFormValid) {
      showToast({
        priority: 'error',
        content: 'Please select an accounting system',
        autoClose: 5000,
      });
      setIsLoading(false);
      return;
    }

    // Handle location setup
    // For R365/Xero: simplified logic - checkbox determines both values
    // For others: use the dropdown selection
    let captureLocation, locationAttr;
    if (useSimplifiedLocationSetup) {
      // Simplified: checkbox sets both capture_location and location_attr
      captureLocation = showLocationSetup;
      locationAttr = showLocationSetup;
    } else {
      // Standard: use dropdown selection
      captureLocation =
        locationSetup === 'Class' || locationSetup === 'Location';
      locationAttr = locationSetup === 'Location';
    }

    // Generate app name based on whether it's the first integration
    // First integration: Use the company name if available, otherwise software name
    // Subsequent integrations: Use "Integration N - Software Name" format
    let appName;
    const isFirst = isFirstIntegration
      ? isFirstIntegration()
      : !apps || apps.length === 0;
    if (isFirst) {
      // Use company name for first integration if available
      appName = companyName || integrationTypeName;
    } else {
      const integrationNumber = (apps?.length || 0) + 1;
      appName = `Integration ${integrationNumber} - ${integrationTypeName}`;
    }

    // Build request body for v2 API
    const requestBody = {
      app_name: appName,
      integration_type: integrationType,
      capture_location: captureLocation,
      location_attr: locationAttr,
    };

    // Add optional period fields if accounting periods are enabled
    if (showPeriodTypes) {
      requestBody.period_cal = true;
      // Use periodCalType if set, otherwise fall back to defaultPeriod
      const periodValue = periodCalType || defaultPeriod;
      // Ensure period_cal_type is always a string, not an array
      requestBody.period_cal_type = Array.isArray(periodValue)
        ? periodValue[0]
        : periodValue;
    }

    try {
      const response = await fetchWithAuth('/api/v2/integrations/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.error) {
        throw new Error(data.error || 'Unknown error occurred');
      }

      // Resolve the integration ID consistently - API may return it in different places
      // Note: For newly created integrations, the API returns company_id (not company_id_id)
      // company_id_id is used for existing integrations in the list
      const integrationId = data.integration?.id || data.last_integration_id;
      const companyId = data.integration?.company_id || data.company_id;

      // Track integration added event - use same isFirst logic computed above
      trackIntegrationAdded({
        integrationId: integrationId || '',
        integrationType: integrationType,
        appName: appName,
        isFirstIntegration: isFirst,
      });

      showToast({
        priority: 'success',
        content: 'Integration added successfully',
        autoClose: 5000,
      });

      if (onSave) {
        onSave(data);
      }

      // Initiate connection based on integration type

      if (integrationType === 'online' && integrationId) {
        // QuickBooks Online uses OAuth
        await initiateQuickBooksConnection(integrationId);
      } else if (hotglueFlowId && sourceId && integrationId && companyId) {
        // Other integrations (R365, Xero, etc.) use HotGlue
        await initiateHotglueConnection(integrationId, companyId);
      } else {
        // No auto-connection needed (edge case)
        setIsLoading(false);
        resetFormState();
        onClose();
      }
    } catch (error) {
      console.error('Error adding integration:', error);
      showToast({
        priority: 'error',
        content: `Error adding integration: ${error.message || 'Unknown error'}`,
        autoClose: 5000,
      });
      setIsLoading(false);
    }
  };

  const initiateQuickBooksConnection = async integrationId => {
    try {
      // Store integration ID for callback handler
      localStorage.setItem('integration_id', integrationId);

      // Store integration details for analytics tracking
      localStorage.setItem('integration_type', integrationType);
      localStorage.setItem('integration_app_name', integrationTypeName);

      // Generate OAuth URL
      const data = new FormData();
      data.append('integration_id', integrationId);
      data.append('callback_url', `${window.location.origin}/callback`);

      const response = await fetchWithAuth('/api/inuit_auth/', {
        method: 'POST',
        body: data,
      });

      // Handle non-OK responses
      if (!response.ok) {
        let errorBody = '';
        try {
          errorBody = await response.text();
        } catch (_e) {
          // ignore body read errors
        }
        throw new Error(
          `Failed to initiate QuickBooks connection: ${response.status} ${response.statusText}${
            errorBody ? ` - ${errorBody}` : ''
          }`
        );
      }

      // Parse OAuth URL from response
      let oauthUrl;
      try {
        oauthUrl = await response.json();
      } catch (_e) {
        throw new Error(
          'Invalid response received from server while initiating QuickBooks connection.'
        );
      }

      // Reset loading state and close modal before redirecting
      setIsLoading(false);
      resetFormState();
      onClose();

      // Redirect to the QuickBooks OAuth page (forces account chooser).
      redirectToIntuitOAuth(oauthUrl);
    } catch (error) {
      console.error('Error initiating QuickBooks connection:', error);
      showToast({
        priority: 'error',
        content:
          'Integration added but failed to initiate connection. Please connect manually.',
        autoClose: 5000,
      });
      setIsLoading(false);
      resetFormState();
      onClose();
    }
  };

  /**
   * Initiate HotGlue connection for non-QuickBooks integrations (Xero, R365, etc.)
   */
  const initiateHotglueConnection = async (integrationId, companyId) => {
    try {
      // Build tenant ID as used by HotGlue: companyId_integrationId
      const tenantId = `${companyId}_${integrationId}`;

      // Store integration details for reference
      localStorage.setItem('integration_id', integrationId);
      localStorage.setItem('integration_type', integrationType);
      localStorage.setItem('integration_app_name', integrationTypeName);

      // HotGlue options with listeners
      const hotglueOptions = {
        hideBackButtons: true,
        breadcrumbs: false,
        listener: {
          onSourceLinked: () => {
            // Connection successful - call backend to update status
            connectIntegration(integrationId);
          },
          onPopupClose: () => {
            setIsLoading(false);
          },
          onWidgetClose: () => {
            setIsLoading(false);
          },
        },
      };

      // Reset state and close modal before opening HotGlue widget
      setIsLoading(false);
      resetFormState();
      onClose();

      if (typeof link === 'function') {
        link(tenantId, hotglueFlowId, sourceId, false, hotglueOptions);
      } else {
        throw new Error('HotGlue widget not initialized');
      }
    } catch (error) {
      console.error('Error initiating HotGlue connection:', error);
      showToast({
        priority: 'error',
        content:
          'Integration added but failed to initiate connection. Please connect manually.',
        autoClose: 5000,
      });
      setIsLoading(false);
      resetFormState();
      onClose();
    }
  };

  /**
   * Call backend to mark integration as connected after HotGlue link
   */
  const connectIntegration = async integrationId => {
    try {
      const data = new FormData();
      data.append('integration_id', integrationId);

      await fetchWithAuth('/api/connect_auth/', {
        method: 'POST',
        body: data,
      });
    } catch (error) {
      console.error('Error connecting integration:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add integration"
      className={`${theme.className || ''} add-integration-modal`}
    >
      <ModalBody className="add-integration-modal-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Integration Type Selector */}
          <div>
            <BodyText emphasis style={{ marginBottom: '8px' }}>
              Accounting system
            </BodyText>
            <SelectMenu
              options={formatSelectData(INTEGRATION_TYPE_OPTIONS)}
              placeholder="Select Accounting System"
              value={integrationType || null}
              onChange={(_, value) => handleIntegrationTypeChange(value)}
            />
          </div>

          {/* Period Types - Checkbox with conditional dropdown */}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <Checkbox
              id="periodTypes"
              checked={showPeriodTypes}
              onChange={() => {
                setShowPeriodTypes(!showPeriodTypes);
                if (!showPeriodTypes) {
                  setPeriodCalType(defaultPeriod);
                }
              }}
              label="Use accounting periods"
            />
            {showPeriodTypes && (
              <div>
                <BodyText emphasis style={{ marginBottom: '8px' }}>
                  Period type
                </BodyText>
                <SelectMenu
                  options={formatSelectData(PERIOD_TYPE_OPTIONS)}
                  placeholder={defaultPeriod}
                  value={periodCalType || defaultPeriod}
                  onChange={(_, value) => setPeriodCalType(value)}
                />
              </div>
            )}
          </div>

          {/* Location Setup - Checkbox with conditional dropdown */}
          {/* For R365/Xero: simplified checkbox only (no dropdown) */}
          {/* For others: checkbox + dropdown for Class/Location/Neither */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Checkbox
              id="locationSetup"
              checked={showLocationSetup}
              onChange={() => {
                setShowLocationSetup(!showLocationSetup);
                if (!showLocationSetup) {
                  setLocationSetup('Neither');
                }
              }}
              label={`I manage multiple locations in ${integrationTypeName || 'my accounting system'}`}
            />
            {/* Only show dropdown for non-simplified types (QuickBooks) */}
            {showLocationSetup && !useSimplifiedLocationSetup && (
              <div>
                <BodyText emphasis style={{ marginBottom: '8px' }}>
                  Location setup
                </BodyText>
                <SelectMenu
                  options={[
                    { value: 'Class', label: 'Class' },
                    { value: 'Location', label: 'Location' },
                    { value: 'Neither', label: 'Neither' },
                  ]}
                  placeholder="Select location setup"
                  value={locationSetup}
                  onChange={(_, value) => setLocationSetup(value)}
                />
                <BodyText
                  size="small"
                  style={{
                    marginTop: '8px',
                    color: 'var(--semantic-color-text-neutral-lightest-none)',
                  }}
                >
                  Unsure? Choose "Neither". You can update this later.
                </BodyText>
              </div>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="tonal" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          isLoading={isLoading}
          disabled={!isFormValid}
        >
          Add & Connect
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ReskinnedAddIntegrationFlowModal.propTypes = {
  /** Whether the modal is open */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when modal is closed */
  onClose: PropTypes.func.isRequired,
  /** Callback when integration is saved successfully */
  onSave: PropTypes.func,
  /** Default period type value */
  defaultPeriod: PropTypes.string,
  /** Full integration type data including HotGlue flow_id and source_id */
  allIntegrationTypes: PropTypes.arrayOf(
    PropTypes.shape({
      integration_type: PropTypes.string,
      flow_id: PropTypes.string,
      source_id: PropTypes.string,
    })
  ),
  /** Function to check if this is the first integration */
  isFirstIntegration: PropTypes.func,
  /** Array of existing integrations */
  apps: PropTypes.array,
  /** Company name from metadata for naming first integration */
  companyName: PropTypes.string,
};

ReskinnedAddIntegrationFlowModal.defaultProps = {
  onSave: undefined,
  defaultPeriod: '13 periods',
  allIntegrationTypes: [],
  isFirstIntegration: undefined,
  apps: [],
  companyName: '',
};

export default ReskinnedAddIntegrationFlowModal;
